import { styled } from '@linaria/react';
import { Card, Group, Menu, ScrollArea, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import {
  useBookmarkGoalMutation,
  useUnbookmarkGoalMutation,
} from '../../../../api/strategy-client/strategy-client.hooks';
import type { Goal, Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../containers/notifications/notifications';
import { secondaryBase, skyDark, skyLight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { SimplifiedOutcomesTable } from './simplified-outcomes-table';

type Props = {
  goal: Goal;
  outcomes: Outcome[];
};

export const GoalOverviewSimplifiedWidget = ({ goal, outcomes }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();

  const bookmarkMutation = useBookmarkGoalMutation();
  const unbookmarkMutation = useUnbookmarkGoalMutation();
  const isBookmarkPending = bookmarkMutation.isPending || unbookmarkMutation.isPending;
  const isBookmarked = goal.is_bookmarked || false;

  const handleBookmarkToggle = () => {
    if (isBookmarked) {
      unbookmarkMutation.mutate(goal.id, {
        onError: () => {
          toast.error(i18n.t('common.bookmark_error'));
        },
      });
    } else {
      bookmarkMutation.mutate(goal.id, {
        onError: () => {
          toast.error(i18n.t('common.bookmark_error'));
        },
      });
    }
  };

  return (
    <Card
      shadow="lg"
      radius="md"
      withBorder
      style={{ display: 'flex', flexDirection: 'column', height: '400px' }}
      padding="lg"
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <Stack gap={5} style={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
          <Group gap={8} align="center" wrap="nowrap" style={{ width: '100%' }}>
            <Tooltip variant="black" label={goal.name} zIndex={1000}>
              <Text
                size="large"
                weight="medium"
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {goal.name}
              </Text>
            </Tooltip>
            <Menu shadow="md" width={200} position="bottom-end" zIndex={1000}>
              <Menu.Target>
                <ActionMenuButton>
                  <Icon name="more_horiz" size={18} color={skyDark} style={{ cursor: 'pointer' }} />
                </ActionMenuButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<Icon name="arrow_forward" size={16} color={secondaryBase} />}
                  onClick={() => navigate(`/application/strategy/overview?goalId=${goal.id}`)}
                >
                  <Text size="small">View details</Text>
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    isBookmarkPending ? (
                      <Loader size="xs" />
                    ) : (
                      <Icon
                        name={isBookmarked ? 'bookmark_added' : 'bookmark_add'}
                        size={16}
                        color={isBookmarked ? secondaryBase : skyDark}
                      />
                    )
                  }
                  onClick={handleBookmarkToggle}
                  disabled={isBookmarkPending}
                >
                  <Text size="small">
                    {isBookmarked ? i18n.t('common.remove_bookmark') : i18n.t('common.add_bookmark')}
                  </Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Stack>
      </div>

      {/* Scrollable table container */}
      <ScrollArea flex={1} type="hover" scrollbarSize={0} style={{ width: '100%', overflow: 'hidden' }}>
        <SimplifiedOutcomesTable outcomes={outcomes} />
      </ScrollArea>
    </Card>
  );
};

const ActionMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: 1px solid ${skyLight};
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-color: ${secondaryBase};
  }
`;
